import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import type { BadgeUniverso, JoinedSession } from 'shared';
import {
  ACTIVITY_SESSION_JOIN_PATH,
  ACTIVITY_TYPE_LABELS,
  BADGE_UNIVERSO_COLORS,
  parseActivitySessionQR,
} from 'shared';
import { useJoinSession } from '@/hooks/useActivities';
import { joinSessionErrorMessage, SESSION_QR_NOT_RECOGNISED_MESSAGE } from '@/lib/errors';

type Status =
  | { kind: 'working' }
  | { kind: 'failed'; message: string }
  | { kind: 'joined'; session: JoinedSession };

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
};

const formatDay = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
};

/**
 * "15:00–17:00", or "10/10, 09:00 – 11/10, 17:00" for an open day that lasts
 * more than a day.
 */
const formatSpan = (startsAt: string, endsAt: string): string => {
  if (formatDay(startsAt) === formatDay(endsAt)) {
    return `${formatTime(startsAt)}–${formatTime(endsAt)}`;
  }
  return `${formatDay(startsAt)}, ${formatTime(startsAt)} – ${formatDay(endsAt)}, ${formatTime(endsAt)}`;
};

/**
 * Landing page for the QR code a session's leader shows on the first slide.
 *
 * The code is a deep link (…/join/:token), so a phone camera opens this route
 * directly and the in-app scanner routes here too. Arriving is the whole
 * gesture: the participant is recorded as present and shown what the activity
 * is, who runs it and what they will need. Nothing else has to be scanned for
 * the rest of the session.
 *
 * The route sits behind ProtectedRoute, so a participant who is not signed in
 * detours through login and is returned here to finish joining.
 */
export default function JoinActivityPage() {
  const { token } = useParams<{ token: string }>();
  const join = useJoinSession();
  const [status, setStatus] = useState<Status>({ kind: 'working' });

  // Guards against the effect firing twice (React StrictMode double-invoke).
  // A second request would be harmless, but there is no reason to send it.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const parsed = parseActivitySessionQR(`${ACTIVITY_SESSION_JOIN_PATH}${token ?? ''}`);
    if (parsed === null) {
      setStatus({ kind: 'failed', message: SESSION_QR_NOT_RECOGNISED_MESSAGE });
      return;
    }

    void (async () => {
      try {
        const session = await join.mutateAsync(parsed);
        setStatus({ kind: 'joined', session });
      } catch (err) {
        setStatus({ kind: 'failed', message: joinSessionErrorMessage(err) });
      }
    })();
    // Runs once on mount; the ref above is the real guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Atividade · DIGGER</title>
      </Helmet>

      {status.kind === 'working' && (
        <div className="flex flex-col items-center gap-4 py-10 text-center" role="status">
          <span
            aria-hidden="true"
            className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-[#512f89]"
          />
          <p className="text-sm text-muted-foreground">A entrar na atividade…</p>
        </div>
      )}

      {status.kind === 'failed' && (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <p
            role="alert"
            className="rounded-xl border border-busy/50 bg-busy-soft p-3 text-sm text-busy"
          >
            {status.message}
          </p>
          <HomeLink />
        </div>
      )}

      {status.kind === 'joined' && <JoinedCard session={status.session} />}
    </div>
  );
}

function HomeLink() {
  return (
    <Link
      to="/"
      replace
      className="inline-flex w-full items-center justify-center rounded-xl bg-[#512f89] px-4 py-3 text-sm font-bold text-white transition-transform active:scale-[0.99]"
    >
      Ir para o início
    </Link>
  );
}

function JoinedCard({ session }: { session: JoinedSession }) {
  return (
    <>
      <section
        aria-live="polite"
        className="rounded-2xl border border-available bg-available-soft p-4 shadow-sm"
      >
        <p className="text-xs font-bold uppercase tracking-wider text-available">
          Presença registada
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-extrabold text-foreground">{session.activityName}</h1>
          {session.universo && <UniversoChip universo={session.universo} />}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {ACTIVITY_TYPE_LABELS[session.activityType]} · {session.digitalCenterName}
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {formatSpan(session.startsAt, session.endsAt)} · com {session.leaderName}
        </p>
      </section>

      <p className="px-1 text-sm text-muted-foreground">
        Durante a atividade não precisa de ler o código dos equipamentos: use o que lhe for
        indicado.
      </p>

      {session.description && <Detail title="Sobre a atividade" text={session.description} />}
      {session.syllabus && <Detail title="Programa" text={session.syllabus} />}
      {session.tools && <Detail title="Ferramentas" text={session.tools} />}

      <HomeLink />
    </>
  );
}

function Detail({ title, text }: { title: string; text: string }) {
  return (
    <section className="card p-4">
      <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h2>
      <p className="mt-1 whitespace-pre-line text-sm text-foreground">{text}</p>
    </section>
  );
}

function UniversoChip({ universo }: { universo: BadgeUniverso }) {
  const { bg, text } = BADGE_UNIVERSO_COLORS[universo];
  return (
    <span
      className="flex-shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ backgroundColor: bg, color: text }}
    >
      {universo}
    </span>
  );
}
